import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { SectionTitle, EmptyState, Modal } from "@/components/Brutal";
import { toast } from "sonner";
import { Save, Search, Filter, DollarSign, AlertTriangle, CheckCircle2, History } from "lucide-react";

const UNIDADES = { km117: "KM 117 Anambé", cupari: "Rio Cuparí", sede: "SEDE — Santarém" };

export default function ComprasPriceBulk() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [onlyZero, setOnlyZero] = useState(true);
  const [unidadeFilter, setUnidadeFilter] = useState("");
  const [drafts, setDrafts] = useState({}); // id → new preco string
  const [savingId, setSavingId] = useState(null);
  const [showSaved, setShowSaved] = useState({}); // id → true (verde por 2s)
  const [historyProduct, setHistoryProduct] = useState(null); // {product, rows}
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (p) => {
    setHistoryProduct({ product: p, rows: [] });
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/products/${p.id}/price-history`);
      setHistoryProduct({ product: p, rows: data });
    } catch {
      toast.error("Erro ao carregar histórico");
    }
    setHistoryLoading(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      // X-Unidade: all → busca as duas unidades
      const { data } = await api.get("/products", { headers: { "X-Unidade": "all" } });
      setProducts(data);
    } catch { toast.error("Erro ao carregar produtos"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const preco = Number(p.preco_custo || 0);
      if (onlyZero && preco > 0) return false;
      if (unidadeFilter && p.filial !== unidadeFilter) return false;
      if (q && !(p.nome || "").toLowerCase().includes(q) && !(p.codigo || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, query, onlyZero, unidadeFilter]);

  const totals = useMemo(() => {
    const total = products.length;
    const zerados = products.filter(p => Number(p.preco_custo || 0) === 0).length;
    return { total, zerados, ajustados: total - zerados };
  }, [products]);

  const setDraft = (id, val) => setDrafts((d) => ({ ...d, [id]: val }));

  const save = async (p) => {
    const raw = drafts[p.id];
    if (raw === undefined) return;
    const preco = parseFloat(String(raw).replace(",", "."));
    if (isNaN(preco) || preco < 0) { toast.error("Preço inválido"); return; }
    setSavingId(p.id);
    try {
      await api.put(`/products/${p.id}`, { preco_custo: preco });
      setProducts((cur) => cur.map((x) => x.id === p.id ? { ...x, preco_custo: preco } : x));
      setDrafts((d) => { const c = { ...d }; delete c[p.id]; return c; });
      setShowSaved((s) => ({ ...s, [p.id]: true }));
      setTimeout(() => setShowSaved((s) => { const c = { ...s }; delete c[p.id]; return c; }), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSavingId(null);
  };

  const saveAll = async () => {
    const items = Object.keys(drafts);
    if (items.length === 0) { toast.info("Nenhum preço alterado"); return; }
    let ok = 0, fail = 0;
    for (const pid of items) {
      const p = products.find((x) => x.id === pid);
      if (!p) continue;
      const raw = drafts[pid];
      const preco = parseFloat(String(raw).replace(",", "."));
      if (isNaN(preco) || preco < 0) { fail++; continue; }
      try {
        await api.put(`/products/${pid}`, { preco_custo: preco });
        setProducts((cur) => cur.map((x) => x.id === pid ? { ...x, preco_custo: preco } : x));
        ok++;
      } catch { fail++; }
    }
    setDrafts({});
    if (ok) toast.success(`${ok} preço(s) salvo(s)`);
    if (fail) toast.error(`${fail} falharam`);
  };

  const draftCount = Object.keys(drafts).length;

  return (
    <div data-testid="compras-precos">
      <SectionTitle
        action={
          draftCount > 0 && (
            <button onClick={saveAll} data-testid="save-all-btn" className="brutal-btn bg-[#1A7F37] text-white px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" /> Salvar {draftCount} alterado(s)
            </button>
          )
        }
      >
        Ajuste de Preços em Lote
      </SectionTitle>

      {/* Estatísticas */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="brutal-card p-3">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1"><DollarSign className="w-3 h-3" /> Total de produtos</div>
          <div className="font-display font-black text-3xl leading-none mt-1">{totals.total}</div>
        </div>
        <div className="brutal-card p-3 border-l-4 border-[#E60000]" data-testid="stat-zerados">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#E60000] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Sem preço (R$ 0)</div>
          <div className="font-display font-black text-3xl leading-none mt-1 text-[#E60000]">{totals.zerados}</div>
          <div className="font-mono-data text-[10px] text-[#777] mt-1">Corrigir antes do backfill</div>
        </div>
        <div className="brutal-card p-3 border-l-4 border-[#1A7F37]">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#1A7F37] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Com preço ajustado</div>
          <div className="font-display font-black text-3xl leading-none mt-1 text-[#1A7F37]">{totals.ajustados}</div>
          <div className="font-mono-data text-[10px] text-[#777] mt-1">{totals.total > 0 ? Math.round(totals.ajustados / totals.total * 100) : 0}% do cadastro</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="brutal-card p-3 mb-4 flex flex-wrap gap-3 items-center" data-testid="price-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#777]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por código ou nome..."
            className="w-full border-2 border-[#111] pl-8 pr-2 py-2 text-sm"
            data-testid="price-search"
          />
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={onlyZero} onChange={(e) => setOnlyZero(e.target.checked)} className="w-4 h-4 accent-[#FF4F00]" data-testid="only-zero-toggle" />
          <span className="font-mono-data text-[11px] uppercase tracking-widest text-[#111]">Só zerados</span>
        </label>
        <select
          value={unidadeFilter}
          onChange={(e) => setUnidadeFilter(e.target.value)}
          className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data"
          data-testid="unit-filter"
        >
          <option value="">Todas unidades</option>
          <option value="km117">KM 117 Anambé</option>
          <option value="cupari">Rio Cuparí</option>
          <option value="sede">SEDE — Santarém</option>
        </select>
        <div className="ml-auto font-mono-data text-[10px] uppercase tracking-widest text-[#777]">
          <Filter className="w-3 h-3 inline mr-1" /> {filtered.length} produto(s)
        </div>
      </div>

      {/* Tabela */}
      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>Unidade</th>
              <th>Categoria</th>
              <th className="text-right">Estoque</th>
              <th className="text-right w-48">Preço Custo (R$)</th>
              <th className="w-24">Ação</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#777] font-mono-data text-xs">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message={onlyZero ? "🎉 Nenhum produto zerado com os filtros atuais" : "Nenhum produto encontrado"} cols={7} />}
            {!loading && filtered.map((p) => {
              const cur = Number(p.preco_custo || 0);
              const draft = drafts[p.id];
              const isDirty = draft !== undefined && String(parseFloat(String(draft).replace(",", "."))) !== String(cur);
              return (
                <tr key={p.id} data-testid={`price-row-${p.id}`} className={showSaved[p.id] ? "bg-[#E6F4EA]" : ""}>
                  <td className="font-mono-data text-xs">{p.codigo}</td>
                  <td>{p.nome}</td>
                  <td className="font-mono-data text-xs">{UNIDADES[p.filial] || p.filial}</td>
                  <td className="text-xs text-[#555]">{p.categoria}</td>
                  <td className="text-right font-mono-data">{p.estoque_atual}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <span className="font-mono-data text-xs text-[#777]">R$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draft !== undefined ? draft : (cur > 0 ? cur.toFixed(2) : "")}
                        onChange={(e) => setDraft(p.id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(p); }}
                        placeholder="0,00"
                        className={`w-24 border-2 p-1.5 text-sm text-right font-mono-data ${
                          cur === 0 ? "border-[#E60000] bg-[#FFF5F5]" : "border-[#111]"
                        } ${isDirty ? "bg-[#FFF8E1]" : ""}`}
                        data-testid={`price-input-${p.id}`}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {isDirty ? (
                        <button
                          onClick={() => save(p)}
                          disabled={savingId === p.id}
                          className="px-2 py-1 border-2 border-[#1A7F37] bg-[#1A7F37] text-white text-xs font-bold hover:opacity-80 inline-flex items-center gap-1"
                          data-testid={`save-price-${p.id}`}
                        >
                          <Save className="w-3 h-3" /> {savingId === p.id ? "..." : "Salvar"}
                        </button>
                      ) : showSaved[p.id] ? (
                        <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#1A7F37] inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Salvo
                        </span>
                      ) : cur === 0 ? (
                        <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#E60000]">Pendente</span>
                      ) : (
                        <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#777]">—</span>
                      )}
                      <button
                        onClick={() => openHistory(p)}
                        title="Histórico de preços"
                        className="p-1 border-2 border-[#111] hover:bg-[#0A66C2] hover:text-white"
                        data-testid={`history-price-${p.id}`}
                      >
                        <History className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 border-2 border-[#FFC107] bg-[#FFF8E1] text-sm text-[#5A3E00] font-mono-data" data-testid="price-hint">
        💡 <strong>Fluxo recomendado:</strong> Preencha os preços dos produtos zerados aqui → rode o script de backfill (`python /app/backend/scripts/backfill_movement_values.py`) → os valores aparecem em Movimentações retroativamente.
      </div>

      {/* Modal Histórico de Preços */}
      {historyProduct && (
        <Modal open={true} onClose={() => setHistoryProduct(null)} title={`Histórico de Preço · ${historyProduct.product.codigo} · ${historyProduct.product.nome}`} size="lg">
          <div className="space-y-3" data-testid="price-history-modal">
            <div className="brutal-card-sm p-3 bg-[#F5F5F5]">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Preço atual</div>
              <div className="font-mono-data text-2xl font-bold text-[#1A7F37]">R$ {Number(historyProduct.product.preco_custo || 0).toFixed(2)}</div>
            </div>
            {historyLoading && <div className="text-center py-6 text-[#777] font-mono-data text-xs">Carregando...</div>}
            {!historyLoading && historyProduct.rows.length === 0 && (
              <div className="text-center py-6 text-[#777] font-mono-data text-xs">Nenhuma alteração de preço registrada ainda.</div>
            )}
            {!historyLoading && historyProduct.rows.length > 0 && (
              <table className="brutal-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Quem</th>
                    <th className="text-right">De</th>
                    <th className="text-right">Para</th>
                    <th className="text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {historyProduct.rows.map((r) => (
                    <tr key={r.id} data-testid={`history-row-${r.id}`}>
                      <td className="font-mono-data text-xs">{String(r.changed_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="text-sm">{r.changed_by_name || "—"} <span className="text-[10px] text-[#777] font-mono-data uppercase">({r.changed_by_role})</span></td>
                      <td className="text-right font-mono-data text-xs text-[#777]">R$ {Number(r.old_price || 0).toFixed(2)}</td>
                      <td className="text-right font-mono-data font-bold">R$ {Number(r.new_price || 0).toFixed(2)}</td>
                      <td className={`text-right font-mono-data text-xs font-bold ${r.delta > 0 ? "text-[#E60000]" : "text-[#1A7F37]"}`}>
                        {r.delta > 0 ? "+" : ""}{Number(r.delta || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
